import { Alert, Heading, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import {
  CraAttributes as Defs,
  CraAttributeSelect,
  CraAttributeNumber,
  CraAttributeTextArea,
} from "@keita/lanepatrol-sdk";
import { useStores } from "../../../../../shared/stores/RootStore";
import { ImageSelectAttribute } from "./attributes/ImageSelectAttribute";
import { NumberAttribute } from "./attributes/NumberAttribute";
import { TextAreaAttribute } from "./attributes/TextAreaAttribute";
import { AdjacentFeaturesAttribute } from "./attributes/AdjacentFeaturesAttribute";
import { SuggestionConditions } from "../../../../../coding/utils/attribute-suggestions";
import { CodingView } from "../../../../../coding/models/CodingView";

const groupLabelPrefix = "sdk:attribute.group";

const CodingConsoleBase = ({ disabled }: { disabled: boolean }) => {
  const { codingStore, surveyStore } = useStores();
  const { distanceUnit } = surveyStore;
  const nodeCoding = codingStore.getFromSelectedNode()!;
  const { attributes } = nodeCoding;

  const compact = codingStore.view === CodingView.Enum.Compact;

  const imageSelectAttr = (
    definition: CraAttributeSelect,
    attributeDisabled = false,
    optional = false,
  ) => (
    <ImageSelectAttribute
      nodeCoding={nodeCoding}
      attributeDefinition={definition}
      optional={optional}
      attributeDisabled={attributeDisabled}
      readOnly={disabled}
      compact={compact}
    />
  );

  const numberAttr = (
    definition: CraAttributeNumber,
    attributeDisabled = false,
  ) => (
    <NumberAttribute
      nodeCoding={nodeCoding}
      attributeDefinition={definition}
      attributeDisabled={attributeDisabled}
      readOnly={disabled}
      compact={compact}
      distanceUnit={distanceUnit}
    />
  );

  const textAreaAttr = (
    definition: CraAttributeTextArea,
    attributeDisabled = false,
  ) => (
    <TextAreaAttribute
      nodeCoding={nodeCoding}
      attributeDisabled={attributeDisabled}
      readOnly={disabled}
      compact={compact}
      attributeDefinition={definition}
    />
  );

  const adjacentFeaturesAttr = (subname: string, attributeDisabled = false) => (
    <AdjacentFeaturesAttribute
      disabled={disabled || attributeDisabled}
      subName={subname}
      compact={compact}
      optional={false}
      nodeCoding={nodeCoding}
    />
  );

  const buildTitle = (groupLabel: string) => (
    <Heading size="xs" textTransform="uppercase" textAlign="center">
      {t(`${groupLabelPrefix}.${groupLabel}`)}
    </Heading>
  );

  const noDelineation = SuggestionConditions.noDelineation(attributes);
  const noIntersectionOrRoadCrossing =
    SuggestionConditions.noIntersectionOrRoadCrossing(attributes);
  const noAdjacentRoadLane =
    SuggestionConditions.noAdjacentRoadLane(attributes);

  const gridGap = "16px";
  const minChildWidth = compact ? "400px" : "200px";

  return (
    <Stack overflow="auto" w="100%" p={4} gap={6}>
      {disabled && (
        <Alert.Root status="info">
          <Alert.Indicator />
          <Text fontSize="sm">
            You do not have permissions to code. To change this, please speak to
            the route manager.
          </Text>
        </Alert.Root>
      )}
      <Stack>
        {buildTitle("facilityFeatures")}

        <SimpleGrid minChildWidth={minChildWidth} gap={gridGap}>
          {imageSelectAttr(Defs.facilityType)}
          {imageSelectAttr(Defs.delineation, noDelineation)}
          {imageSelectAttr(Defs.facilityAccess)}
          {imageSelectAttr(Defs.looseOrSlipperySurface)}
          {imageSelectAttr(Defs.tramOrTrainRails)}
          {imageSelectAttr(Defs.majorSurfaceDeformationOrDrainOpening)}
          {imageSelectAttr(Defs.fixedObstacleOnFacility)}
          {imageSelectAttr(Defs.nonFixedObstacleOnFacility)}
          {imageSelectAttr(Defs.lineOfSight)}
          {imageSelectAttr(Defs.lightSegregation)}
          {imageSelectAttr(Defs.facilityWidthPerDirection)}
          {imageSelectAttr(Defs.flowDirection)}
          {imageSelectAttr(Defs.widthRestriction)}
          {adjacentFeaturesAttr("roadLane")}
          {imageSelectAttr(Defs.numberOfLanesAdjacentRoad, noAdjacentRoadLane)}
          {adjacentFeaturesAttr("vehicleParking")}
          {adjacentFeaturesAttr("severeHazard")}
          {adjacentFeaturesAttr("objectOrLevelChange")}
          {adjacentFeaturesAttr(
            "sidewalk",
            SuggestionConditions.noAdjacentSidewalk(attributes),
          )}
          {imageSelectAttr(Defs.grade)}
          {imageSelectAttr(Defs.curvature)}
          {imageSelectAttr(Defs.streetLighting)}
          {imageSelectAttr(Defs.pedestrianCrossing)}
          {imageSelectAttr(Defs.intersectionBicycleFacility)}
          {imageSelectAttr(Defs.intersectionOrRoadCrossing)}
          {imageSelectAttr(
            Defs.intersectionApproach,
            noIntersectionOrRoadCrossing,
          )}
          {imageSelectAttr(Defs.crossingFacility, noIntersectionOrRoadCrossing)}
          {imageSelectAttr(
            Defs.numberOfLanesIntersectingRoad,
            noIntersectionOrRoadCrossing,
          )}
          {imageSelectAttr(Defs.propertyAccess)}
        </SimpleGrid>
      </Stack>

      <Stack>
        {buildTitle("speedVolume")}

        <SimpleGrid minChildWidth={minChildWidth} gap={gridGap}>
          {imageSelectAttr(Defs.peakPedestrianFlowAlongOrAcrossFacility)}
          {imageSelectAttr(Defs.peakBicycleAndLVTrafficFlow)}
          {imageSelectAttr(Defs.observedProportionOfCargoBikesAndMopeds)}
          {imageSelectAttr(Defs.bicycleAndLVSpeedAverage)}
          {imageSelectAttr(Defs.bicycleAndLVSpeedDifferential)}
          {imageSelectAttr(Defs.heavyVehicleFlow, noAdjacentRoadLane)}
          {numberAttr(Defs.roadAADT, noAdjacentRoadLane)}
          {numberAttr(Defs.roadSpeedLimit, noAdjacentRoadLane)}
          {numberAttr(Defs.roadOperatingSpeed, noAdjacentRoadLane)}
        </SimpleGrid>
      </Stack>

      <Stack>
        {buildTitle("optionalAttributes")}

        <SimpleGrid minChildWidth={minChildWidth} gap={gridGap}>
          {imageSelectAttr(Defs.areaType, false, true)}
          {textAreaAttr(Defs.comment, false)}
        </SimpleGrid>
      </Stack>
    </Stack>
  );
};

export const CodingConsole = observer(CodingConsoleBase);
